import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import CompanyFooter from '@/components/Companies/CompanyFooter';
import CompanyInvoiceForm from '@/components/Companies/CompanyInvoiceForm/CompanyInvoiceForm';
import { DefaultRouteTabs, SaveButton } from '@/components/_common';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { CompanyInvoiceSettingsForm } from '@/core/domain/invoice/settings/Settings.types';
import Authorities from '@/types/Authority';
import { PageProps } from '@/types/PageProps';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Form, message } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

const initialTabErrors = {
  details: false,
  aliquot: false,
  invoiceNfe: false,
  invoiceNfc: false,
  invoiceNfs: false,
  certificate: false,
};

export type CompanyInvoiceSettingsTabErrors = typeof initialTabErrors;

const CompanyInvoicePage = ({ route }: PageProps) => {
  const intl = useIntl().formatMessage;
  const [tabErrors, setTabErrors] = useState(initialTabErrors);
  const [loadingValidate, setLoadingValidate] = useState(false);

  const { isLoading, companyId, formValues, forms, handleSubmit, disabled } =
    useCompanyFormContext();

  const [form] = Form.useForm<CompanyInvoiceSettingsForm>(forms.companyInvoice);

  const showNFMessage = Form.useWatch('showNFMessage', form);

  const { data: invoiceSettings, dataUpdatedAt } = useInvoiceSettingsByCompany(
    Number(companyId),
  );

  useEffect(() => {
    if (formValues.companyInvoice) {
      const { nfsLogin, nfsPassword, nfs, ...companyInvoice } =
        formValues.companyInvoice;

      form.setFieldsValue({
        ...companyInvoice,
        nfs: { ...nfs, login: nfsLogin, password: nfsPassword },
      });
    } else if (invoiceSettings && !formValues.companyInvoice) {
      form.setFieldsValue({
        ...invoiceSettings,
        nfc: invoiceSettings.configNfc,
        nfe: invoiceSettings.configNfe,
        nfs: {
          ...invoiceSettings.configNfs,
          startDate: invoiceSettings.configNfs?.startDate
            ? moment(invoiceSettings.configNfs?.startDate)
            : undefined,
          withheldDefault:
            typeof invoiceSettings.configNfs?.withheldDefault === 'boolean'
              ? String(invoiceSettings.configNfs?.withheldDefault)
              : undefined,
        },
        culturalIncentive: invoiceSettings.culturalIncentivator,
      });
    }
  }, [dataUpdatedAt, formValues.companyGeneral]);

  function handleTabError(key: keyof typeof initialTabErrors) {
    setTabErrors((state) => ({ ...state, [key]: true }));
  }

  async function onSubmit() {
    setLoadingValidate(true);
    try {
      setTabErrors(initialTabErrors);

      const details = await form
        .validateFields(['specialTaxRegime', 'certificateId'])
        .catch(() => handleTabError('details'));

      const invoiceNfe = await form
        .validateFields([
          ['nfe', 'series'],
          ['nfe', 'initialNumber'],
        ])
        .catch(() => handleTabError('invoiceNfe'));

      const invoiceNfc = await form
        .validateFields([
          ['nfc', 'series'],
          ['nfc', 'initialNumber'],
          ['nfc', 'securityCodeNfce'],
          ['nfc', 'cldToken'],
        ])
        .catch(() => handleTabError('invoiceNfc'));

      const invoiceNfs = await form
        .validateFields([
          ['nfs', 'series'],
          ['nfs', 'initialRpsNumber'],
        ])
        .catch(() => handleTabError('invoiceNfs'));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hasDataValues = (data: any) =>
        Object.values(data).every((value) => !!value);

      if (
        !details ||
        !hasDataValues(details) ||
        !invoiceNfe ||
        !hasDataValues(invoiceNfe) ||
        !invoiceNfc ||
        !hasDataValues(invoiceNfc) ||
        !invoiceNfs ||
        !hasDataValues(invoiceNfs)
      ) {
        throw new Error('Preencha todos os campos');
      }

      handleSubmit();
    } catch {
      message.error('Há campos a serem validados!');
    } finally {
      setLoadingValidate(false);
    }
  }

  return (
    <DefaultRouteTabs
      baseKey={String(companyId)}
      permissions={route.permissions}
      basePath={`/app/registry/companies/${companyId}`}
      route={route.parent}
      extra={
        <>
          <SaveButton
            type="primary"
            onClick={onSubmit}
            disabled={
              disabled ||
              !!showNFMessage ||
              loadingValidate ||
              !hasPermissions([Authorities.REGISTER_COMPANY_EDIT])
            }
            loading={loadingValidate || isLoading}
          >
            {intl({ id: 'commons.entity.save' })}
          </SaveButton>
        </>
      }
    >
      <CompanyInvoiceForm
        form={form}
        tabErrors={tabErrors}
        key={dataUpdatedAt}
        onSubmit={onSubmit}
        loadingValidate={loadingValidate || isLoading}
      />

      <CompanyFooter disabled={disabled} />
    </DefaultRouteTabs>
  );
};

export default CompanyInvoicePage;
